<template>
  <div class="invoicing-view">
    <!-- Invoice data -->
    <grid-details-component
      v-if="customer && invoiceCustomer"
      :display_spec="invoiceCustomerSpecs"
      :item="invoiceCustomer"
      :item_keys="customer_invoicing_keys">
    </grid-details-component>
    <!-- Loader -->
    <loader-component v-else></loader-component>
    <!-- Messages -->
    <message-component v-if="customer && !customer.isConnectedToInvoiceCustomer" :level="'warning'">
      <strong>
        <translate>No invoice customer chosen!</translate>
      </strong>
      <br/>
      <translate>Connect the customer to an existing invoice customer or create a new one below.</translate>
    </message-component>
    <message-component v-if="customer && customer.isConnectedToInvoiceCustomer && invoiceCustomer" :level="'info'">
      <span v-translate>This customer is connected to a invoice customer</span>
      <link-component
        :name="'InvoiceCustomerCustomerListView'"
        :params="{ invoiceCustomerId: customer.invoiceCustomerId }">
        {{ invoiceCustomer.companyName }}
      </link-component>
      <button class="button is-outlined is-pulled-right is-large" @click="disconnectInvoiceCustomer()" v-translate>
        Disconnect the invoice customer
      </button>
    </message-component>
    <!-- Connect an existing invoice customer -->
    <div v-if="showInvoiceCustomerForm">
<!--          </title-component>-->
      <div class="line-under grid-box">
        <title-component
          :size="4">
              <translate :translate-params="{ count: searched_invoiceCustomers.length, org_no: invoice_customer_search_term ? invoice_customer_search_term : invoiceCustomer.orgNumber }">
              Invoice customers filtered on orgnumber %{org_no}
              </translate>
        </title-component>
      </div>
      <div class="grid-box">
        <label>
          <translate>Search invoice customer:</translate>
        </label>
        <input
        ref="search" class="input" type="text"
        :value="invoice_customer_search_term ? invoice_customer_search_term : invoiceCustomer.orgNumber"
        v-on:keyup.enter="searchCustomNumber">
      </div>
      <div v-for="(item, index) in searched_invoiceCustomers" :key="index" class="invoice-customer columns is-mobile is-5">
        <div class="column is-12-mobile is-10-tablet is-10-desktop">
          <grid-details-component
            :display_spec="invoiceCustomerSpecs"
            :item="item"
            :item_keys="invoice_customer_match_keys">
          </grid-details-component>
        </div>
        <div class="column is-12-mobile is-2-tablet is-2-desktop">
          <button class="button is-outlined is-large" @click="showConfirmationModal(item)" v-translate>
            Choose
          </button>
        </div>
      </div>
      <message-component v-if="searched_invoiceCustomers.length === 0" :level="'info'">
        <translate> No matching invoice customers found based on supplied invoice information</translate>
      </message-component>
    </div>
    <!-- Confirmation pop-up box -->
    <card-modal-component
      :show="showInvoiceCustomerForm && show_confirmation_modal"
      @deactivate="hideConfirmationModal()"
      transition="fade"
      ref="modal"
      :options="{ hasOkBtn: { text: 'Choose' }, hasCancelBtn: { enabled: true }}"
      @handleOk="chooseInvoiceCustomer($event)"
      @handleCancel="hideConfirmationModal()"
      :title="'Confirmation'">
      <div slot="card-content" class="field is-grouped">
        <translate :translate-params="{ customerId: customer.id, orgNumber: selected_invoiceCustomer.orgNumber }">
          Are you sure you want to connect customer %{customerId} with invoice customer: %{orgNumber}
        </translate>
      </div>
    </card-modal-component>
    <!-- Connect a new invoice customer -->
    <div class="invoice-customer-form" v-if="showInvoiceCustomerForm">
      <title-component
        :size="4"
        :lineSeparator="'under'">
        <translate> Create a new invoice customer</translate>
      </title-component>
      <form-component
        v-for="formDescriptor in formDescriptors"
        :formDescriptor="formDescriptor"
        :formData="getFormDataForDescriptor(formDescriptor)"
        :key="formDescriptor.id">
      </form-component>
      <submit-btn-component
        ref="enterButton"
        class="is-primary margin-top is-uppercase is-large"
        @submit="createInvoiceCustomer" v-translate>
        Create invoice customer
      </submit-btn-component>
    </div>
    <!-- Invoice history -->
    <table-component-buefy
      v-if="loaded && invoices.length"
      :content="invoices"
      :headings="table_headings"
      :item_keys="table_keys"
      :display_spec="invoiceSpecs"
      :total_items="total_items"
      :hide_table_search="true"
      :pagination_callback="loadInvoices"
      class="veridens-dashboard">
      <translate>Invoice history</translate>
    </table-component-buefy>
  </div>
</template>
<script>
  import { mapActions, mapState, mapGetters } from 'vuex'
  import GridDetailsComponent from '@/components/layout/grid/GridDetailsComponent.vue'
  import { invoiceCustomerSpecs, invoiceSpecs } from '@/display_context/display_context'
  import { itemRefresher, formHandlerMixin } from '@/mixins/mixins'
  import { updateArrayForValue, globalEventBus } from '@/utility/utility_functions'
  import MessageComponent from '@/components/layout/utility/MessageComponent'
  import TableComponentBuefy from '@/components/layout/table/TableComponentBuefy'
  import TitleComponent from '@/components/layout/TitleComponent'
  import FormComponent from '@/components/form/FormComponent'
  import SubmitBtnComponent from '@/components/layout/utility/SubmitBtnComponent'
  import CardModalComponent from '@/components/layout/modal/CardModalComponent'
  import LinkComponent from '@/components/layout/utility/LinkComponent'
  import LoaderComponent from '@/components/layout/utility/LoaderComponent'

  export default {
    name: 'invoicing-view',
    mixins: [itemRefresher, formHandlerMixin],
    components: {
      GridDetailsComponent,
      MessageComponent,
      TableComponentBuefy,
      TitleComponent,
      FormComponent,
      SubmitBtnComponent,
      CardModalComponent,
      LinkComponent,
      LoaderComponent
    },
    data () {
      return {
        invoiceCustomerSpecs: invoiceCustomerSpecs,
        invoiceSpecs: invoiceSpecs,
        customer_invoicing_keys: ['reference', 'marking'],
        invoice_customer_match_keys: ['companyName', 'orgNumber', 'postalAddress', 'email'],
        searched_invoiceCustomers: [],
        selected_invoiceCustomer: {},
        table_headings: ['Invoice No.', 'Created', 'Amount', 'State'],
        table_keys: ['invoiceLink', 'created', 'totalSum', 'state'],
        total_items: 0,
        loaded: false,
        setAction: this.setInvoiceCustomer,
        invoiceCustomer: null,
        invoicesLoaded: false,
        show_confirmation_modal: false,
        invoice_customer_search_term: null
      }
    },
    props: {
      customerId: {
        required: true
      }
    },
    computed: {
      ...mapState({
        invoices: state => state.invoiceModule.invoices,
        invoiceCustomers: state => state.invoiceCustomerModule.invoiceCustomers,
        formDescriptors: state => state.customerModule.invoiceCustomerFormDescriptors,
        formDatas: state => state.customerModule.invoiceCustomerFormDatas
      }),
      ...mapGetters([
        'getCustomerForId'
      ]),
      companyInvoicing () {
        return this.customer.invoicing && !this.customer.invoicing.useCompanyAddress
      },
      showInvoiceCustomerForm () {
        return this.customer.id && !this.customer.isConnectedToInvoiceCustomer && this.invoiceCustomer
      },
      customer () {
        return this.getCustomerForId(this.customerId) || {}
      }
    },
    methods: {
      ...mapActions([
        'setCustomer',
        'setInvoiceCustomer',
        'getInvoices',
        'getInvoiceCustomersForOrgNumber',
        'setupCustomerInvoiceCustomerCreateForm',
        'postCustomerInvoiceCustomerCreateForm',
        'deleteInvoiceCustomerOnCustomer',
        'setCurrentInvoiceCustomerOnCustomer'
      ]),
      showConfirmationModal (invoiceCustomer) {
        this.selected_invoiceCustomer = invoiceCustomer
        this.show_confirmation_modal = true
      },
      disconnectInvoiceCustomer () {
        this.deleteInvoiceCustomerOnCustomer(this.customer.id).then(() => {
          this.setCustomer(this.customer.id).then(() => {
            this.updateInvoiceCustomer()
            globalEventBus.$emit('setActions')
          }).catch((err) => {
            this.handleError(err)
          })
        }).catch((err) => {
          this.handleError(err)
        })
      },
      hideConfirmationModal () {
        this.show_confirmation_modal = false
      },
      chooseInvoiceCustomer (btn) {
        let requestData = {
          customerId: this.customer.id,
          data: { 'invoice_customer': this.selected_invoiceCustomer.id }
        }
        this.setCurrentInvoiceCustomerOnCustomer(requestData).then(() => {
          this.setCustomer(this.customer.id).then(() => {
            btn.enable()
            this.updateInvoiceCustomer()
            this.hideConfirmationModal()
            globalEventBus.$emit('setActions')
          }).catch((err) => {
            this.handleError(err)
          })
        }).catch((err) => {
          this.handleError(err)
        })
      },
      createInvoiceCustomer (btn) {
        let requestData = {
          customerId: this.customer.id,
          formDatas: this.formDatas
        }
        this.submitRequest = this.postCustomerInvoiceCustomerCreateForm
        this.submitForm(requestData, btn, this.createInvoiceCustomerCallback)
      },
      createInvoiceCustomerCallback () {
        this.setCustomer(this.customer.id).then(() => {
          this.updateInvoiceCustomer()
        }).catch((err) => {
          this.handleError(err)
        })
      },
      searchCustomNumber (event) {
        this.invoice_customer_search_term = event.target.value
        this.getInvoiceCustomers()
      },
      getInvoiceCustomers () {
        if (this.customer && this.customer.invoicing) {
          let number = this.customer.invoicing.useCompanyAddress ? this.customer.invoicing.orgNumber : this.customer.personNumber
          if (this.invoice_customer_search_term) {
            number = this.invoice_customer_search_term
          }
          if (number) {
            this.getInvoiceCustomersForOrgNumber(number).then((result) => {
              this.searched_invoiceCustomers = result
            }).catch((err) => {
              this.handleError(err)
            })
          }
        }
      },
      adjustInvoiceCustomer () {
        // use customer data instead of company data
        // TODO: Probably this should be handled on the backend already
        this.invoiceCustomer.reference = this.customer.invoicing.reference
        this.invoiceCustomer.marking = this.customer.invoicing.marking
        this.invoiceCustomer.distribution = this.customer.invoicing.distribution
        if (!this.invoiceCustomer.useCompanyAddress) {
          this.invoiceCustomer.customerName = this.customer.fullName
          this.invoiceCustomer.orgNumber = this.customer.personNumber
          updateArrayForValue(this.customer_invoicing_keys, { 'companyName': 'customerName' })
        }
      },
      loadInvoices (requestData) {
        let filter = {
          customer: this.customer.id
        }
        return this.getInvoices(Object.assign(filter, requestData)).then((items) => {
          this.total_items = items.count
          this.loaded = true
          return items
        }).catch((err) => {
          this.handleError(err)
        })
      },
      setupInvoiceCustomerCreateForm () {
        this.setupCustomerInvoiceCustomerCreateForm(this.customer.id).catch((err) => {
          this.handleError(err)
        })
      },
      updateInvoiceCustomer () {
        if (this.customer.isConnectedToInvoiceCustomer) {
          this.setOrRefreshItem('invoiceCustomer', this.invoiceCustomers, this.customer.invoiceCustomerId, [this.adjustInvoiceCustomer])
        } else {
          // TODO: It gets confusing when we re-use invoiceCustomer and fill it with other data
          this.invoiceCustomer = this.customer.invoicing
          this.adjustInvoiceCustomer()
        }
      },
      loadCallbacks () {
        this.updateInvoiceCustomer()
        this.getInvoiceCustomers()
        this.setupInvoiceCustomerCreateForm()
        this.loadInvoices()
      },
      emitCustomerChanged () {
        if (this.customer.id) {
          this.$emit('customerChanged')
        }
      }
    },
    mounted () {
      this.$once('customerChanged', this.loadCallbacks)
      this.emitCustomerChanged()
    },
    watch: {
      customer () {
        this.emitCustomerChanged()
      }
    }
  }
</script>

<style lang="sass" scoped>
  .invoice-customer
    background: #FBFDFD
    border: 1px solid #BCE8F1
    padding: 1rem 0
    margin: 0

  .invoice-customer-form
    margin-top: 2rem

  .margin-top
    margin-top: 2rem
</style>
